import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraOverlayDrawer } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { PageBody, PageHero } from '../../../components/layout';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { resolveCompanyPlaceholder, useTenantIdentity } from '../_shared/hooks/useTenantIdentity';
import { useAuditTemplates, useUpsertAuditTemplate } from './hooks';
const areaOptions = [
    {
        value: 'Program',
        label: 'Program'
    },
    {
        value: 'Contracts',
        label: 'Contracts'
    },
    {
        value: 'Accounting',
        label: 'Accounting'
    }
];
const columns = [
    {
        key: 'name',
        header: 'Name'
    },
    {
        key: 'area',
        header: 'Area'
    },
    {
        key: 'questions',
        header: 'Questions',
        render: (row)=>`${row.questions.length}`
    },
    {
        key: 'isDefault',
        header: 'Default',
        render: (row)=>row.isDefault ? 'Yes' : 'No'
    },
    {
        key: 'isSystem',
        header: 'System',
        render: (row)=>row.isSystem ? 'Yes' : 'No'
    }
];
const fields = [
    {
        kind: 'text',
        name: 'name',
        label: 'Template name',
        required: true,
        maxLength: 200
    },
    {
        kind: 'select',
        name: 'area',
        label: 'Area',
        required: true,
        options: areaOptions
    },
    {
        kind: 'checkbox',
        name: 'isDefault',
        label: 'Default for this area'
    },
    {
        kind: 'textarea',
        name: 'question1',
        label: 'Question 1',
        rows: 2
    },
    {
        kind: 'textarea',
        name: 'question2',
        label: 'Question 2 (optional)',
        rows: 2
    },
    {
        kind: 'textarea',
        name: 'question3',
        label: 'Question 3 (optional)',
        rows: 2
    }
];
/**
 * Read-only template detail. System templates have no edit surface, so this
 * drawer is the only place users (and QA) can inspect the questions a
 * system-level audit plan will ask — fed entirely from the list cache, no
 * extra RPC.
 */ function TemplateDetailSheet({ template, onOpenChange }) {
    // Shared _system templates say {{company}} instead of any one tenant's
    // name — render it as the viewer's tenant alias (nickname, else full name).
    // Not gated on the identity query: the neutral fallback is safe to show and
    // must not block the drawer if GetMe is slow or failing.
    const { identity } = useTenantIdentity();
    const open = !!template;
    // The parent nulls the template on close while the drawer stays mounted —
    // snapshot the last open record so the exit animation keeps its content.
    const last = useRef(template);
    if (template) last.current = template;
    const shown = template ?? last.current;
    const questions = [
        ...shown?.questions ?? []
    ].sort((a, b)=>a.sortOrder - b.sortOrder);
    return /*#__PURE__*/ _jsxs(SpecraOverlayDrawer, {
        open: open,
        onOpenChange: (next)=>{
            if (!next) onOpenChange(false);
        },
        title: shown?.name,
        description: "Audit plan questions, in order.",
        testId: Testids.deliveryDocs.templateDetailDrawer,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-[18px] flex flex-wrap gap-[6px]",
                children: [
                    shown?.area && /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "outline",
                        children: shown.area
                    }),
                    shown?.isDefault && /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: "Default"
                    }),
                    shown?.isSystem && /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "dark",
                        children: "System"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                children: "Questions"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 space-y-3",
                children: [
                    questions.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "This template has no questions."
                    }),
                    questions.map((q, i)=>/*#__PURE__*/ _jsxs("div", {
                            className: "rounded-md border p-3 text-sm",
                            "data-testid": Testids.deliveryDocs.templateDetailQuestion,
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "mr-2 font-medium text-muted-foreground",
                                    children: [
                                        i + 1,
                                        "."
                                    ]
                                }),
                                resolveCompanyPlaceholder(q.question, identity?.alias)
                            ]
                        }, q.id || i))
                ]
            })
        ]
    });
}
export function AuditTemplatesPage() {
    const tenantId = useTenantId();
    const { data, isLoading, error, refetch } = useAuditTemplates(tenantId);
    const upsert = useUpsertAuditTemplate();
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [viewing, setViewing] = useState(null);
    const handleSubmit = async (values)=>{
        const questions = [
            values.question1,
            values.question2,
            values.question3
        ].map((q, i)=>({
                id: editing?.questions[i]?.id ?? '',
                question: q ?? '',
                sortOrder: i + 1
            })).filter((q)=>q.question.trim() !== '');
        await upsert.mutateAsync({
            id: editing?.id ?? '',
            companyId: tenantId,
            name: values.name,
            area: values.area,
            isDefault: Boolean(values.isDefault),
            questions
        });
        setFormOpen(false);
    };
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.deliveryDocs.templatesPage,
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: "Audit Templates",
                    subtitle: "Reusable audit definitions and their question sets."
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex justify-end",
                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>{
                            setEditing(null);
                            setFormOpen(true);
                        },
                        "data-testid": Testids.deliveryDocs.templateCreate,
                        children: "New Template"
                    })
                }),
                /*#__PURE__*/ _jsx(EntityTable, {
                    data: data?.items ?? [],
                    columns: columns,
                    testid: Testids.deliveryDocs.templatesTable,
                    rowTestid: Testids.deliveryDocs.templateRow,
                    rowKey: (t)=>t.id,
                    isLoading: isLoading,
                    error: error,
                    onRetry: ()=>void refetch(),
                    onRowClick: (row)=>setViewing(row),
                    rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-end gap-1",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    onClick: ()=>setViewing(row),
                                    "data-testid": Testids.deliveryDocs.templateView,
                                    children: "View"
                                }),
                                !row.isSystem && /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    onClick: ()=>{
                                        setEditing(row);
                                        setFormOpen(true);
                                    },
                                    "data-testid": Testids.deliveryDocs.templateEdit,
                                    children: "Edit"
                                })
                            ]
                        })
                }),
                /*#__PURE__*/ _jsx(TemplateDetailSheet, {
                    template: viewing,
                    onOpenChange: (open)=>{
                        if (!open) setViewing(null);
                    }
                }),
                /*#__PURE__*/ _jsx(EntityFormDialog, {
                    open: formOpen,
                    onOpenChange: setFormOpen,
                    title: editing ? 'Edit Template' : 'New Audit Template',
                    fields: fields,
                    defaultValues: {
                        id: editing?.id ?? '',
                        name: editing?.name ?? '',
                        area: editing?.area ?? '',
                        isDefault: editing?.isDefault ?? false,
                        question1: editing?.questions[0]?.question ?? '',
                        question2: editing?.questions[1]?.question ?? '',
                        question3: editing?.questions[2]?.question ?? ''
                    },
                    onSubmit: handleSubmit,
                    isSubmitting: upsert.isPending
                })
            ]
        })
    });
}
